/**
 * KAN-702: одна строка списка материалов оператора: превью, тексты, правка, удаление.
 * KAN-717: флажок "Прикладывать к письму" (PATCH attach_to_email).
 * KAN-721: документы (иконка, имя файла, размер), флажки строки в
 * MaterialRowFlags, плашка "Основной прайс" и подсветка основного прайс-листа.
 */
import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type {
  InstructionAttachment,
  UpdateInstructionAttachmentPayload,
} from '@/services/api/instructionAttachments';
import { formatFileSize, isImageMaterial, materialFileName } from './materialFiles';
import { MaterialFileLink } from './MaterialFileLink';
import { MaterialPreview } from './MaterialPreview';
import { MaterialRowFlags } from './MaterialRowFlags';

interface InstructionAttachmentRowProps {
  item: InstructionAttachment;
  onUpdate: (data: UpdateInstructionAttachmentPayload) => Promise<unknown>;
  onDelete: () => void;
}

export function InstructionAttachmentRow({
  item,
  onUpdate,
  onDelete,
}: InstructionAttachmentRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const isMainPrice = item.is_main_price ?? false;
  const size = formatFileSize(item.size_bytes);

  const startEdit = () => {
    setTitle(item.title);
    setDescription(item.description);
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await onUpdate({ title: title.trim(), description: description.trim() });
      setEditing(false);
    } catch {
      // ошибку показывает toast в мутации, режим правки остаётся
    }
  };

  return (
    <li
      data-testid={`attachment-${item.id}`}
      data-main-price={isMainPrice ? 'true' : undefined}
      className={`flex gap-3 rounded-md border p-2 ${
        isMainPrice ? 'border-green-500 bg-green-50' : ''
      }`}>
      {isImageMaterial(item) ? (
        <MaterialFileLink item={item} title="Открыть в новой вкладке" className="flex-shrink-0">
          <MaterialPreview item={item} />
        </MaterialFileLink>
      ) : (
        <MaterialPreview item={item} />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        {editing ? (
          <>
            <Input
              aria-label="Название материала"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
            <Textarea
              aria-label="Описание материала"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={saveEdit} disabled={!title.trim()}>
                Сохранить
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
                Отмена
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium break-words">{item.title}</p>
              {isMainPrice && <Badge variant="completed">Основной прайс</Badge>}
            </div>
            {!isImageMaterial(item) && (
              <p data-testid={`attachment-${item.id}-file`} className="text-xs break-all">
                <MaterialFileLink item={item} className="text-blue-700 hover:underline">
                  {materialFileName(item)}
                </MaterialFileLink>
                {size && <span className="text-muted-foreground">, {size}</span>}
              </p>
            )}
            {item.description && (
              <p className="text-xs text-muted-foreground break-words">{item.description}</p>
            )}
          </>
        )}
        <MaterialRowFlags item={item} onUpdate={onUpdate} />
        {confirmingDelete && (
          <div className="flex items-center gap-2 text-xs">
            <span>Удалить материал?</span>
            <Button type="button" size="sm" variant="destructive" onClick={onDelete}>
              Удалить
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setConfirmingDelete(false)}>
              Нет
            </Button>
          </div>
        )}
      </div>
      {!editing && (
        <div className="flex flex-shrink-0 items-start gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Изменить материал ${item.title}`}
            onClick={startEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Удалить материал ${item.title}`}
            onClick={() => setConfirmingDelete(true)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </li>
  );
}
